<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { CsEmptyState } from '../../empty-state';
import { CsIcon } from '../../icon';
import { CsInput } from '../../input';
import { CsListbox, CsListboxItem } from '../../listbox';
import {
  CsPopover,
  CsPopoverCard,
  CsPopoverCardContent,
  CsPopoverContent,
  CsPopoverTrigger,
} from '../../popover';
import { toolbarButtonItemRecipe } from './toolbarRecipe';

/**
 * @typedef {object} VariableProps
 * @property {boolean} [isDisabled=false] - Whether the variable picker button is disabled
 * @property {('default'|'inverse')} [variant='default'] - Color variant for the icon
 * @property {Array} [variables=[]] - Array of variables with {id, label, value, description} structure
 */

/**
 * @type {VariableProps}
 */
const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'default',
  },
  variables: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['select']);

const search = ref('');
const searchInput = ref(null);
const highlightedIndex = ref(0);
const listContainerRef = ref(null);
const isPopoverOpen = ref(false);

// Filter variables based on search
const filteredVariables = computed(() => {
  if (!search.value) {
    return props.variables;
  }

  const searchTerm = search.value.toLowerCase();
  return props.variables.filter(
    variable =>
      variable.label.toLowerCase().includes(searchTerm) ||
      variable.description?.toLowerCase().includes(searchTerm)
  );
});

const hasResults = computed(() => filteredVariables.value.length > 0);

function adjustScrollPosition() {
  const container = listContainerRef.value;
  const selectedElement = container?.querySelector('[data-highlighted="true"]');

  if (container && selectedElement) {
    const containerRect = container.getBoundingClientRect();
    const elementRect = selectedElement.getBoundingClientRect();

    if (elementRect.top < containerRect.top) {
      selectedElement.scrollIntoView({ block: 'center' });
    } else if (elementRect.bottom > containerRect.bottom) {
      selectedElement.scrollIntoView({ block: 'center' });
    }
  }
}

// Reset highlighted index when filtered results change
watch(filteredVariables, () => {
  highlightedIndex.value = 0;
  nextTick(() => adjustScrollPosition());
});

function onSelectVariable(variable) {
  emit('select', variable);
  isPopoverOpen.value = false;
}

function handleKeyDown(event) {
  if (!hasResults.value) return;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      highlightedIndex.value = Math.min(
        filteredVariables.value.length - 1,
        highlightedIndex.value + 1
      );
      nextTick(() => adjustScrollPosition());
      break;
    case 'ArrowUp':
      event.preventDefault();
      highlightedIndex.value = Math.max(0, highlightedIndex.value - 1);
      nextTick(() => adjustScrollPosition());
      break;
    case 'Enter':
      event.preventDefault();
      if (filteredVariables.value[highlightedIndex.value]) {
        onSelectVariable(filteredVariables.value[highlightedIndex.value]);
      }
      break;
    case 'Escape':
      event.preventDefault();
      isPopoverOpen.value = false;
      break;
    default:
      break;
  }
}

function handleOpenPopover() {
  isPopoverOpen.value = true;
  search.value = '';
  highlightedIndex.value = 0;
  nextTick(() => {
    searchInput.value?.focus();
  });
}

function handleClosePopover() {
  isPopoverOpen.value = false;
  search.value = '';
  highlightedIndex.value = 0;
}
</script>

<template>
  <CsPopover
    is-manual
    :is-open="isPopoverOpen"
    is-teleport
    placement="bottom-start"
    class="flex"
    @open="handleOpenPopover"
    @close="handleClosePopover"
  >
    <CsPopoverTrigger @click="handleOpenPopover">
      <button
        data-component="CsEditorToolbarVariable"
        :class="
          toolbarButtonItemRecipe({
            ...props,
            isActive: isPopoverOpen,
          })
        "
        class="gap-1 !w-auto !px-1.5"
        :disabled="props.isDisabled"
      >
        Variables
        <CsIcon name="code-variable" />
      </button>
    </CsPopoverTrigger>

    <CsPopoverContent>
      <CsPopoverCard>
        <CsPopoverCardContent class="min-w-[400px]">
          <div class="p-2">
            <CsInput
              ref="searchInput"
              v-model="search"
              placeholder="Search variables..."
              @keydown="handleKeyDown"
            />
          </div>

          <CsEmptyState
            v-if="!hasResults"
            icon-type="no-result-found"
            heading="No results found"
            content="Your search didn't return any results."
            class="py-8"
          />

          <div
            v-else
            ref="listContainerRef"
            class="max-h-[300px] overflow-y-auto"
          >
            <CsListbox>
              <CsListboxItem
                v-for="(variable, index) in filteredVariables"
                :key="variable.id"
                :is-manual-highlight="true"
                :is-hovered="index === highlightedIndex"
                :is-highlighted="index === highlightedIndex"
                @mouseenter="highlightedIndex = index"
                @click="onSelectVariable(variable)"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="bg-brand-surface-secondary font-semibold border border-brand-border px-1.5 py-0.5 rounded-md text-xs font-mono tracking-tight shrink-0"
                  >
                    {{ variable.label }}
                  </span>
                  <span class="text-sm text-brand-text-secondary">
                    {{ variable.description }}
                  </span>
                </div>
              </CsListboxItem>
            </CsListbox>
          </div>
        </CsPopoverCardContent>
      </CsPopoverCard>
    </CsPopoverContent>
  </CsPopover>
</template>
